import type { Action, ContentVariant } from "@tracking-web/shared";

/**
 * Aplica las acciones (las 4 adaptaciones) sobre el DOM existente del publisher.
 * Todas las mutaciones se hacen con APIs nativas. El contenido inyectado se
 * marca con data-tw-injected + data-tw-rule para poder medir impresión/clic.
 */

// `url` (opcional): ruta a la que atribuir el evento, sobrescribiendo el
// location.pathname que sella el transporte. Lo usa el tiempo-por-sección para
// atribuir los tramos de la página SALIENTE en una navegación SPA, donde el router
// avisa DESPUÉS de que location ya cambió (ver collectors/sectionTime.ts).
export type EmitFn = (type: string, props?: Record<string, unknown>, url?: string) => void;

type Ctx = {
  variants: Map<string, ContentVariant>;
  emit: EmitFn;
};

/**
 * Resuelve un anchor. Soporta la sintaxis especial "@p:N" = el párrafo nº N del
 * artículo detectado (cuenta todos los <p> en orden), además de selectores CSS.
 */
function resolveAnchor(anchor: string): Element | null {
  if (anchor.indexOf("@p:") === 0) {
    const n = parseInt(anchor.slice(3), 10) || 1;
    const root =
      document.querySelector("article") ||
      document.querySelector("main") ||
      document.body;
    const ps = root.querySelectorAll("p");
    return ps[n - 1] || null;
  }
  return document.querySelector(anchor);
}

function getHtml(v: ContentVariant | undefined): string {
  if (!v) return "";
  const p = v.payload as Record<string, unknown>;
  return typeof p.html === "string" ? p.html : "";
}

/** Envuelve HTML inyectado para marcarlo e instrumentarlo. */
function wrap(html: string, ruleId: string, variantKey: string): HTMLElement {
  const el = document.createElement("div");
  el.setAttribute("data-tw-injected", "");
  el.setAttribute("data-tw-rule", ruleId);
  el.setAttribute("data-tw-variant", variantKey);
  el.style.display = "contents"; // no introduce caja propia → no afecta al layout
  el.innerHTML = html;
  return el;
}

/** Observa la entrada en viewport del nodo inyectado → adaptation_view (una vez). */
function observeView(el: Element, ruleId: string, variantKey: string, emit: EmitFn) {
  if (!("IntersectionObserver" in window)) return;
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          emit("adaptation_view", { ruleId, variantKey });
          io.disconnect();
        }
      }
    },
    { threshold: 0.5 }
  );
  io.observe(el);
}

/** Base CSS responsiva inyectada en el iframe: evita desbordes horizontales y
 *  hace fluidos imágenes/vídeo/tablas, sin pisar el diseño del publisher. */
const SANDBOX_BASE_CSS =
  "<style>*,*::before,*::after{box-sizing:border-box}html,body{margin:0}" +
  "body{overflow-x:hidden}img,video,canvas,svg,table,iframe{max-width:100%;height:auto}</style>";

/**
 * Renderiza código personalizado del publisher (HTML+CSS+JS) dentro de un iframe
 * SANDBOX: `allow-scripts` sin `allow-same-origin` → su JS corre en un origen
 * opaco, aislado: no puede tocar la página, cookies, localStorage ni nuestro
 * sistema. Un reporter inyectado mide la altura y la comunica por postMessage
 * para ajustar el alto del iframe (auto-resize).
 */
function renderSandbox(
  code: string,
  ruleId: string,
  variantKey: string,
  emit: EmitFn
): HTMLElement {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("data-tw-injected", "");
  iframe.setAttribute("data-tw-rule", ruleId);
  iframe.setAttribute("data-tw-variant", variantKey);
  iframe.setAttribute("sandbox", "allow-scripts allow-popups allow-forms");
  iframe.setAttribute("loading", "lazy");
  iframe.style.cssText = "width:100%;border:0;display:block;height:0;overflow:hidden";

  const open = "<scr" + "ipt>";
  const close = "</scr" + "ipt>";
  // Reporter inyectado: (1) mide la altura para auto-resize; (2) PUENTE DE CONVERSIÓN:
  // un clic sobre un elemento interactivo (a/button) o marcado data-tw-goal señala una
  // conversión al exterior por postMessage, SIN exponer nada del contenido del iframe.
  const reporter =
    open +
    "(function(){function p(){try{var H=Math.max(document.documentElement.scrollHeight,(document.body||{}).scrollHeight||0);parent.postMessage({__tw_h:H},'*')}catch(e){}}" +
    "if(window.ResizeObserver){new ResizeObserver(p).observe(document.documentElement)}" +
    "window.addEventListener('load',p);setTimeout(p,50);setTimeout(p,400);setTimeout(p,1200);" +
    "document.addEventListener('click',function(e){var t=e.target;while(t&&t.nodeType===1){if(t.matches&&t.matches('a,button,[role=button],[data-tw-cta],[data-tw-goal]')){try{parent.postMessage({__tw_click:1},'*')}catch(_){}break}t=t.parentNode}},true)})()" +
    close;

  iframe.srcdoc =
    '<!doctype html><html><head><meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width,initial-scale=1">' +
    SANDBOX_BASE_CSS +
    "</head><body>" +
    code +
    reporter +
    "</body></html>";

  function onMsg(e: MessageEvent) {
    if (e.source !== iframe.contentWindow) return;
    const d = e.data as { __tw_h?: number; __tw_click?: number } | null;
    if (!d) return;
    if (typeof d.__tw_h === "number") iframe.style.height = Math.max(0, d.__tw_h) + "px";
    // Conversión dentro del código aislado → se atribuye a esta variante. El sandbox solo
    // postea __tw_click cuando el clic cayó en algo clicable (a/button/[role=button]/
    // data-tw-cta/data-tw-goal), así que siempre es cta:true (cuenta en un test de clic).
    if (d.__tw_click) emit("adaptation_click", { ruleId, variantKey, cta: true });
  }
  window.addEventListener("message", onMsg);
  return iframe;
}

/** ¿La variante es código del publisher marcado para ejecutarse EN LA PÁGINA?
 *  (payload { code, inline: true }). Sin el flag → sandbox. Un SDK antiguo no
 *  conoce el flag y cae a sandbox: degradación segura. */
function isInlineCode(v: ContentVariant | undefined): boolean {
  return !!v && v.type === "code" && (v.payload as Record<string, unknown>).inline === true;
}

/**
 * Re-ejecuta los <script> de un fragmento insertado con innerHTML (que los deja
 * inertes), preservando la semántica de orden del documento: cada script externo
 * SIN `async` bloquea la ejecución de los siguientes hasta que carga (igual que
 * el parser HTML), porque un inline posterior suele depender de él (típico:
 * <script src=lib.js> + <script>lib.init()</script>). Los `async` explícitos no
 * bloquean. Los <script> que no son JS (application/json, templates…) no se tocan.
 * LLAMAR SIEMPRE con el contenedor ya conectado al documento (un script inline
 * solo ejecuta al insertarse en el DOM activo).
 */
function executeScripts(root: ParentNode) {
  const scripts = Array.prototype.slice.call(
    root.querySelectorAll("script")
  ) as HTMLScriptElement[];
  let i = 0;
  function next() {
    while (i < scripts.length) {
      const old = scripts[i++];
      const type = (old.getAttribute("type") || "").toLowerCase();
      const isJs =
        !type || type === "text/javascript" || type === "application/javascript" || type === "module";
      if (!isJs) continue;
      const s = document.createElement("script");
      for (const a of Array.prototype.slice.call(old.attributes) as Attr[]) {
        s.setAttribute(a.name, a.value);
      }
      if (old.src && !old.hasAttribute("async")) {
        // Externo en orden: los siguientes scripts esperan a que cargue (o falle).
        s.async = false;
        s.addEventListener("load", next);
        s.addEventListener("error", next);
        old.replaceWith(s);
        return;
      }
      if (!old.src) s.text = old.textContent || "";
      old.replaceWith(s); // inline ejecuta síncrono aquí; async no bloquea
    }
  }
  next();
}

/**
 * Código del publisher EN LA PÁGINA (sin aislar), modelo Google Tag Manager: el
 * publisher es el dueño del sitio y elige ejecutar su propio JS con acceso total
 * (googletag, adsbygoogle, dataLayer…). NO se sanea: la barrera de seguridad es
 * quién puede escribir en la org, no el contenido. El contenedor lleva
 * data-tw-injected → el collector de clics emite adaptation_click por delegación
 * (las conversiones cuentan igual que en cualquier bloque inline). Nota: el JS
 * corre post-parse, así que document.write rompería la página (se avisa en la UI).
 */
function renderInlineCode(code: string, ruleId: string, variantKey: string): HTMLElement {
  const el = document.createElement("div");
  el.setAttribute("data-tw-injected", "");
  el.setAttribute("data-tw-rule", ruleId);
  el.setAttribute("data-tw-variant", variantKey);
  el.style.display = "contents"; // no introduce caja propia → no afecta al layout
  el.innerHTML = code; // los <script> quedan inertes hasta executeScripts()
  return el;
}

/** ✕ flotante para cerrar un popup (chrome NUESTRO, jamás dentro del HTML del publisher).
 *  - por defecto (`corner` off): pegada a la esquina del `host`, la caja que abraza el contenido
 *    (sandbox/guiado, con ancho propio). Imprescindible en sandbox: el iframe aislado no puede
 *    cerrarse a sí mismo.
 *  - `corner` on: anclada a la esquina superior derecha del OVERLAY a pantalla completa. Se usa
 *    cuando el wrapper ocupa todo el ancho disponible (HTML "bare" y código inline) y por tanto no
 *    abraza a la tarjeta del cliente: así NO tocamos su DOM para colocar la ✕ junto a su contenido. */
function addCloseButton(host: HTMLElement, onClose: () => void, corner = false) {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.setAttribute("aria-label", "Cerrar");
  btn.setAttribute("data-tw-dismiss", "");
  btn.textContent = "✕";
  btn.style.cssText = corner
    ? // `fixed` (no `absolute`): el overlay ahora hace scroll cuando el popup es más alto que el
      // viewport; fijándola al viewport la ✕ no se va con el scroll y siempre queda visible/pulsable.
      "position:fixed;top:16px;right:16px;width:36px;height:36px;border:0;border-radius:9999px;background:#fff;color:#0f172a;font-size:16px;line-height:36px;text-align:center;cursor:pointer;box-shadow:0 4px 14px rgba(0,0,0,.25);z-index:2147483001;padding:0"
    : "position:absolute;top:-14px;right:-14px;width:32px;height:32px;border:0;border-radius:9999px;background:#fff;color:#0f172a;font-size:15px;line-height:32px;text-align:center;cursor:pointer;box-shadow:0 4px 14px rgba(0,0,0,.25);z-index:1;padding:0";
  btn.addEventListener("click", onClose);
  host.appendChild(btn);
}

export function createAdapters(ctx: Ctx) {
  const { variants, emit } = ctx;

  // Teardown de overlays de modal VIVOS (los popups se montan en document.body, FUERA del
  // árbol de render del framework del publisher → una navegación SPA no los retira sola).
  // Cada popup de REGLA registra aquí su cleanup (los de experimentos/bandits NO: ver el `case
  // "modal"`); `teardownOverlays` los desmonta en bloque sin emitir `adaptation_dismiss`
  // (navegar ≠ cerrar). Paridad con MPA, donde la recarga mata el overlay.
  const liveOverlays = new Set<() => void>();
  // Subconjunto EFÍMERO: popups de intención de salida (Atrás / exit-intent de ratón). Se desmontan
  // en CUALQUIER navegación —incluida query/hash con el mismo pathname— no solo en un page view real,
  // para no quedarse pegados sobre la página a la que se vuelve.
  const ephemeralOverlays = new Set<() => void>();
  function teardownOverlays(ephemeralOnly = false) {
    for (const cleanup of Array.from(ephemeralOnly ? ephemeralOverlays : liveOverlays)) cleanup();
  }

  // --- Anti-flicker de condiciones de PAÍS ---
  // El país sale de la IP (solo servidor) → en la 1ª carga de un visitante nuevo hay ~1 RTT antes de
  // conocerlo. Para que una regla/test con condición de país "no muestre nada de lo que afecta hasta
  // saber el país", ocultamos SUS selectores objetivo hasta resolverlo (o hasta un timeout de
  // seguridad, tras el cual manda el resto de condiciones). visibility:hidden (no display:none) para
  // preservar el hueco → CLS = 0. Best-effort: el SDK es async, así que si el contenido ya pintó antes
  // de arrancar, esto minimiza (no elimina del todo) la ventana en que se ve el contenido equivocado.
  // Solo se usa en cache-miss (país aún sin veredicto); un visitante recurrente nunca ve máscara.
  let maskStyle: HTMLStyleElement | null = null;
  const maskSel = new Set<string>(); // acumulado: permite AÑADIR selectores en navegación SPA (re-mask)
  function maskTargets(selectors: string[]) {
    let added = false;
    for (const s of selectors) if (s && !maskSel.has(s)) (maskSel.add(s), (added = true));
    if (!maskSel.size) return; // nada que ocultar
    try {
      if (!maskStyle) {
        maskStyle = document.createElement("style");
        maskStyle.setAttribute("data-tw-geo-mask", "");
        (document.head || document.documentElement).appendChild(maskStyle);
        added = true;
      }
      // Una regla POR selector (no una lista con comas): en CSS un solo selector inválido dentro de
      // una lista separada por comas invalida la lista ENTERA → un selector mal formado dejaría sin
      // máscara a todos. Aislado por regla, un selector inválido solo se ignora a sí mismo.
      if (added) {
        maskStyle.textContent = Array.from(maskSel)
          .map((s) => s + "{visibility:hidden !important}")
          .join("");
      }
    } catch {
      /* entorno sin DOM/append: sin máscara (degradación segura) */
    }
  }
  function revealTargets() {
    try {
      maskStyle?.remove();
    } catch {
      /* noop */
    }
    maskStyle = null;
    maskSel.clear();
  }

  // `ephemeral`: el overlay debe desmontarse al navegar AUNQUE sea de experimento/bandit. Lo usan
  // los popups disparados por la señal de "Atrás" (intención de salir): son efímeros del momento de
  // salida, no contenido persistente → no deben quedar pegados sobre la página a la que se vuelve.
  function apply(action: Action, ruleId: string, ephemeral = false): boolean {
    switch (action.type) {
      case "toggle": {
        const node = document.querySelector<HTMLElement>(action.selector);
        if (!node) return false;
        node.style.display = action.visible ? "" : "none";
        emit("adaptation_applied", { ruleId, type: "toggle", selector: action.selector });
        return true;
      }

      case "insert": {
        const anchor = resolveAnchor(action.anchor);
        if (!anchor) return false;
        const v = variants.get(action.variantKey);
        const inline = isInlineCode(v);
        // Código personalizado del publisher → en la página (inline) o iframe aislado (sandbox).
        const node =
          v && v.type === "code"
            ? inline
              ? renderInlineCode(
                  String((v.payload as Record<string, unknown>).code || ""),
                  ruleId,
                  action.variantKey
                )
              : renderSandbox(
                  String((v.payload as Record<string, unknown>).code || ""),
                  ruleId,
                  action.variantKey,
                  emit
                )
            : wrap(getHtml(v), ruleId, action.variantKey);
        anchor.insertAdjacentElement(action.position as InsertPosition, node);
        if (inline) executeScripts(node); // ya conectado al documento → los <script> ejecutan
        observeView(node, ruleId, action.variantKey, emit);
        emit("adaptation_applied", {
          ruleId,
          type: "insert",
          variantKey: action.variantKey,
        });
        return true;
      }

      case "reorder": {
        const container = document.querySelector(action.container);
        if (!container) return false;
        for (const sel of action.order) {
          const child = container.querySelector(sel);
          if (child) container.appendChild(child); // mueve al final en orden
        }
        emit("adaptation_applied", { ruleId, type: "reorder" });
        return true;
      }

      case "swapMedia": {
        const img = document.querySelector<HTMLImageElement>(action.selector);
        const v = variants.get(action.variantKey);
        if (!img || !v) return false;
        const p = v.payload as Record<string, unknown>;
        // Mantener dimensiones para CLS = 0.
        if (typeof p.src === "string") img.src = p.src;
        if (typeof p.srcset === "string") img.srcset = p.srcset;
        if (typeof p.alt === "string") img.alt = p.alt;
        img.setAttribute("data-tw-rule", ruleId);
        emit("adaptation_applied", { ruleId, type: "swapMedia", variantKey: action.variantKey });
        return true;
      }

      case "replace": {
        // Sustituye el contenido de un elemento existente (tests de retención).
        const node = document.querySelector<HTMLElement>(action.selector);
        const v = variants.get(action.variantKey);
        if (!node || !v) return false;
        node.setAttribute("data-tw-injected", ""); // para atribuir clics dentro
        node.setAttribute("data-tw-rule", ruleId);
        node.setAttribute("data-tw-variant", action.variantKey);
        if (v.type === "code") {
          if (isInlineCode(v)) {
            // Código en la página: el nodo ya está en el documento y ya lleva
            // los data-tw-* (arriba) → basta insertar y re-ejecutar los scripts.
            node.innerHTML = String((v.payload as Record<string, unknown>).code || "");
            executeScripts(node);
          } else {
            // Código personalizado del publisher → iframe aislado dentro del elemento.
            node.innerHTML = "";
            node.appendChild(
              renderSandbox(
                String((v.payload as Record<string, unknown>).code || ""),
                ruleId,
                action.variantKey,
                emit
              )
            );
          }
        } else {
          node.innerHTML = getHtml(v);
        }
        emit("adaptation_applied", { ruleId, type: "replace", selector: action.selector });
        return true;
      }

      case "noop":
        return true; // variante de control: no cambia nada

      case "modal": {
        const v = variants.get(action.variantKey);
        if (!v) return false;
        const payload = v.payload as Record<string, unknown>;
        const dismissible = payload.dismissible !== false;
        // bare = HTML personalizado: renderizamos el HTML del publisher TAL CUAL,
        // sin caja, sin estilos ni botón nuestros (control total).
        const bare = payload.bare === true;

        const overlay = document.createElement("div");
        overlay.setAttribute("data-tw-injected", "");
        overlay.setAttribute("data-tw-rule", ruleId);
        overlay.setAttribute("data-tw-variant", action.variantKey);

        let onKey: ((e: KeyboardEvent) => void) | null = null;
        // cleanup: quita el nodo + el listener de Escape y se da de baja del registro. NO emite
        // evento (lo usa el teardown por navegación). El cierre del usuario lo envuelve con el emit.
        const cleanup = () => {
          overlay.remove();
          if (onKey) document.removeEventListener("keydown", onKey);
          liveOverlays.delete(cleanup);
          ephemeralOverlays.delete(cleanup);
        };
        // Se registran para desmontarse en navegación SPA: los popups de REGLAS, y CUALQUIER popup
        // efímero (intención de salir: Atrás o exit-intent, sea regla, experimento o bandit). Los
        // modales de experimentos (`exp:`) y bandits (`bandit:`) NO-efímeros NO se tocan al navegar →
        // su capa A/B conserva intacta la semántica de exposición/medición. Desmontar un popup efímero
        // de experimento NO pierde la exposición (ya se emitió ANTES de pintar) ni emite dismiss.
        const isExpOrBandit = ruleId.indexOf("exp:") === 0 || ruleId.indexOf("bandit:") === 0;
        if (ephemeral || !isExpOrBandit) liveOverlays.add(cleanup);
        // Los efímeros van además al set que se desmonta en TODA navegación (no solo page views).
        if (ephemeral) ephemeralOverlays.add(cleanup);
        const dismiss = () => {
          cleanup();
          emit("adaptation_dismiss", { ruleId, variantKey: action.variantKey });
        };
        if (dismissible) {
          onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") dismiss();
          };
          document.addEventListener("keydown", onKey);
        }

        if (v.type === "code") {
          // Código personalizado en popup: en la página (inline) o iframe sandbox,
          // siempre sobre el backdrop oscuro.
          const inline = isInlineCode(v);
          // overlay = contenedor con scroll: si el contenido es más alto que el viewport, se hace
          // scroll dentro del overlay (no queda cortado arriba). `margin:auto` en la caja centra
          // cuando cabe y, al desbordar, colapsa a 0 → la caja se alinea al inicio y TODO su
          // contenido es alcanzable con scroll (patrón robusto del modal flex más alto que la pantalla).
          overlay.style.cssText =
            "position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;overflow-y:auto;padding:24px 0;background:rgba(15,23,42,.5)";
          const box = document.createElement("div");
          // inline: el wrapper es un escenario transparente a TODO el ancho disponible (width:92vw), para
          // que el CSS del cliente (p.ej. width:100%;max-width:Xpx) resuelva contra el viewport y respete
          // sus dimensiones — sin `width` colapsaría al ancho del contenido. sandbox: iframe de tamaño fijo.
          box.style.cssText = inline
            ? "position:relative;width:92vw;max-width:92vw;margin:auto"
            : "position:relative;max-width:92vw;width:480px;margin:auto";
          if (inline) {
            box.innerHTML = String(payload.code || "");
          } else {
            box.appendChild(
              renderSandbox(String(payload.code || ""), ruleId, action.variantKey, emit)
            );
          }
          if (dismissible) {
            // inline: el box ocupa todo el ancho → ✕ en la esquina del overlay (no toca su DOM).
            // sandbox: el box abraza el iframe → ✕ pegada a la caja.
            addCloseButton(inline ? overlay : box, dismiss, inline);
            overlay.addEventListener("click", (e) => {
              if (e.target === overlay) dismiss();
            });
          }
          overlay.appendChild(box);
          document.body.appendChild(overlay);
          if (inline) executeScripts(box); // tras conectar al documento
        } else if (bare) {
          // Backdrop oscuro (como el modo guiado) pero SIN caja: tu HTML va tal cual.
          // Solo añadimos una ✕ flotante para poder cerrar.
          // overlay = contenedor con scroll (ver nota en el caso `code`): popup más alto que el
          // viewport → scroll dentro del overlay, `margin:auto` en la caja centra o se alinea al inicio.
          overlay.style.cssText =
            "position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;overflow-y:auto;padding:24px 0;background:rgba(15,23,42,.5)";
          const box = document.createElement("div");
          // Escenario transparente a TODO el ancho disponible (width:92vw). No tocamos el HTML del
          // cliente: solo le damos un contenedor con ancho real para que su width:100%/max-width
          // resuelva contra el viewport y respete sus dimensiones. El centrado lo hace SU propio CSS.
          box.style.cssText = "position:relative;width:92vw;max-width:92vw;margin:auto";
          box.innerHTML = getHtml(v);
          if (dismissible) {
            // ✕ en la esquina del overlay (chrome nuestro): el box ocupa todo el ancho y no abraza su
            // tarjeta, así que no la colgamos dentro de su DOM.
            addCloseButton(overlay, dismiss, true);
            overlay.addEventListener("click", (e) => {
              if (e.target === overlay) dismiss();
            });
          }
          overlay.appendChild(box);
          document.body.appendChild(overlay);
        } else {
          // Modo guiado: caja blanca con estilos por defecto + botón de cierre.
          // overlay = contenedor con scroll (ver nota en el caso `code`): si la caja guiada es más
          // alta que el viewport, se hace scroll dentro del overlay; `margin:auto` la centra o la
          // alinea al inicio al desbordar → su contenido (y la ✕ interna) siempre son alcanzables.
          overlay.style.cssText =
            "position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;overflow-y:auto;padding:24px 0;background:rgba(15,23,42,.5)";
          const box = document.createElement("div");
          box.style.cssText =
            "max-width:90vw;width:420px;background:#fff;border-radius:14px;padding:24px;box-shadow:0 20px 60px rgba(0,0,0,.25);position:relative;margin:auto";
          box.innerHTML = getHtml(v);
          if (dismissible) {
            const close = document.createElement("button");
            close.textContent = "✕";
            close.setAttribute("data-tw-dismiss", "");
            close.style.cssText =
              "position:absolute;top:10px;right:12px;border:0;background:transparent;font-size:18px;cursor:pointer;color:#64748b";
            close.addEventListener("click", dismiss);
            box.appendChild(close);
            overlay.addEventListener("click", (e) => {
              if (e.target === overlay) dismiss();
            });
          }
          overlay.appendChild(box);
          document.body.appendChild(overlay);
        }

        emit("adaptation_view", { ruleId, variantKey: action.variantKey });
        emit("adaptation_applied", { ruleId, type: "modal", variantKey: action.variantKey });
        return true;
      }

      case "quiz": {
        // Quiz nativo interactivo. La config va en la variante (type 'quiz').
        const anchor = resolveAnchor(action.anchor);
        const v = variants.get(action.variantKey);
        if (!anchor || !v) return false;
        const cfg = v.payload as {
          quizId?: string;
          question?: string;
          options?: { value: string; label: string; resultHtml?: string }[];
        };

        const wrap = document.createElement("div");
        wrap.setAttribute("data-tw-injected", "");
        wrap.setAttribute("data-tw-rule", ruleId);
        wrap.setAttribute("data-tw-variant", action.variantKey);
        wrap.setAttribute("data-tw-quiz", cfg.quizId || "quiz");
        wrap.style.cssText =
          "margin:24px 0;padding:20px 22px;border:1px solid #e5e7eb;border-radius:14px;background:#fff";

        const q = document.createElement("p");
        q.textContent = cfg.question || "";
        q.style.cssText = "margin:0 0 14px;font-weight:700;font-size:17px;color:#0f172a";
        wrap.appendChild(q);

        const opts = document.createElement("div");
        opts.style.cssText = "display:flex;flex-wrap:wrap;gap:10px";
        const result = document.createElement("div");
        result.style.cssText = "margin-top:14px";

        (cfg.options || []).forEach((o) => {
          const b = document.createElement("button");
          b.type = "button";
          b.textContent = o.label;
          b.setAttribute("data-tw-quiz-answer", o.value);
          b.style.cssText =
            "border:1px solid #cbd5e1;background:#f8fafc;color:#0f172a;padding:10px 16px;border-radius:10px;cursor:pointer;font-size:15px";
          b.addEventListener("click", () => {
            for (const c of Array.from(opts.children)) {
              (c as HTMLElement).style.borderColor = "#cbd5e1";
              (c as HTMLElement).style.background = "#f8fafc";
            }
            b.style.borderColor = "#2563eb";
            b.style.background = "#eff6ff";
            result.innerHTML = o.resultHtml || ""; // el tracking lo hacen los collectors
          });
          opts.appendChild(b);
        });

        wrap.appendChild(opts);
        wrap.appendChild(result);
        anchor.insertAdjacentElement(action.position as InsertPosition, wrap);
        observeView(wrap, ruleId, action.variantKey, emit);
        emit("adaptation_applied", { ruleId, type: "quiz", variantKey: action.variantKey });
        return true;
      }

      default:
        return false;
    }
  }

  return { apply, teardownOverlays, maskTargets, revealTargets };
}

/**
 * Selectores objetivo de una acción que MUTA contenido existente on-load (los que hay que ocultar en
 * el anti-flicker de país). insert/modal/quiz añaden contenido nuevo o son overlays → no parpadean →
 * no se enmascaran. noop no toca nada.
 */
export function maskSelectorsOfAction(action: Action): string[] {
  switch (action.type) {
    case "toggle":
    case "swapMedia":
    case "replace":
      return [action.selector];
    case "reorder":
      return [action.container];
    default:
      return [];
  }
}
